import { ReactElement } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import Head from 'next/head';
import styled from 'styled-components';
import { talentMapLayout } from '@components/screening/talentMap/layout';
import { CardGrid } from '@components/screening/talentMap/home';
import { TonePill, Tone } from '@components/screening/talentMap/parts';
import {
  Body,
  Card,
  CardTitle,
  Meta,
  PageBody,
  PrimaryButton,
  Row,
  Stack,
  Title,
} from '@components/screening/talentMap/tokens';
import { useJobs } from 'screening/queries';
import { JobSummary } from 'screening/types';
import {
  Skeleton,
  ErrorState,
  EmptyState,
} from '@components/screening/primitives/States';

const Header = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
`;

const OpenLink = styled.a`
  font-weight: 600;
  font-size: 15px;
`;

const jobStatusTone = (status: JobSummary['status']): Tone => {
  if (status === 'published') return 'advance';
  if (status === 'closed') return 'reject';
  return 'neutral';
};

const jobStatusLabel = (status: JobSummary['status']): string =>
  status.charAt(0).toUpperCase() + status.slice(1);

const placeLine = (job: JobSummary) =>
  [job.location, job.work_mode].filter(Boolean).join(' · ');

const countsLine = (job: JobSummary) =>
  `${job.applicant_count} ${
    job.applicant_count === 1 ? 'applicant' : 'applicants'
  }, ${job.cleared_bar_count} cleared the bar`;

const Dashboard = () => {
  const router = useRouter();
  const { data, isLoading, isError, refetch } = useJobs();
  const jobs = data?.jobs ?? [];

  return (
    <PageBody $gap={32}>
      <Head>
        <title>Roles</title>
      </Head>
      <Header data-tour="page-header">
        <Stack $gap={8}>
          <Title>Roles</Title>
          <Body $size={16}>
            Every role you are hiring for, with who has applied and who cleared
            the bar.
          </Body>
        </Stack>
        <PrimaryButton
          type="button"
          data-tour="screening-post-job"
          onClick={() => router.push('/screening/jobs/new')}
        >
          Post a role
        </PrimaryButton>
      </Header>

      {isLoading && (
        <CardGrid $min={420}>
          <Skeleton $h="168px" />
          <Skeleton $h="168px" />
        </CardGrid>
      )}

      {isError && (
        <ErrorState
          title="We could not load your roles"
          onRetry={() => refetch()}
        />
      )}

      {!isLoading && !isError && jobs.length === 0 && (
        <EmptyState
          eyebrow="NOTHING HERE YET"
          title="Post your first role"
          body="Create a role and Melon builds the scoring spec you will screen applicants against."
          action={{
            label: 'Post a role',
            onClick: () => router.push('/screening/jobs/new'),
          }}
        />
      )}

      {jobs.length > 0 && (
        <CardGrid $min={420} data-tour="screening-job-list">
          {jobs.map((job) => (
            <Card key={job.id} style={{ gap: 18 }}>
              <Row
                $between
                style={{ alignItems: 'flex-start', flexWrap: 'nowrap' }}
              >
                <Stack $gap={4}>
                  <CardTitle>{job.role_title}</CardTitle>
                  <Meta>{placeLine(job)}</Meta>
                </Stack>
                <TonePill tone={jobStatusTone(job.status)}>
                  {jobStatusLabel(job.status)}
                </TonePill>
              </Row>
              <Row $between $gap={10} style={{ marginTop: 'auto' }}>
                <Meta>{countsLine(job)}</Meta>
                <Link
                  href={`/screening/jobs/${job.id}`}
                  passHref
                  legacyBehavior
                >
                  <OpenLink>Open role</OpenLink>
                </Link>
              </Row>
            </Card>
          ))}
        </CardGrid>
      )}
    </PageBody>
  );
};

Dashboard.getLayout = function getLayout(page: ReactElement) {
  return talentMapLayout(page);
};

export default Dashboard;
